import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2, Wand2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * SmartContinueDialog — Sprint 7.
 *
 * Two-method UX for "Continue writing":
 *   Method A (cursor / live editor): caller passes `precedingText` and
 *     `followingText` based on the cursor or selection split point.
 *   Method B ("+ Add here" button at end of paragraph in Reading mode): caller
 *     passes paragraph-end as `precedingText` and the rest as `followingText`.
 *
 * Both methods:
 *   1. Show an optional "What should be added?" instruction box (high-level
 *      hint — what topic, how many sentences, which evidence to emphasise).
 *   2. Call `POST /api/thesis/ai/continue` with instruction + following_text.
 *   3. Show generated continuation in a preview pane with Accept / Reject /
 *      Regenerate actions.
 *   4. On Accept, call `onInsert(text)` so the caller can splice it into the
 *      markdown at the original anchor.
 */
export default function SmartContinueDialog({
  open, onOpenChange,
  userId, projectId, language = 'en',
  precedingText = '',
  followingText = '',
  anchorLabel = 'this paragraph',
  defaultInstruction = '',
  onInsert,
}) {
  const [instruction, setInstruction] = useState(defaultInstruction);
  const [maxSentences, setMaxSentences] = useState(3);
  const [continuation, setContinuation] = useState('');
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const taRef = useRef(null);

  useEffect(() => {
    if (open) {
      setInstruction(defaultInstruction);
      setContinuation('');
      setMeta(null);
      setTimeout(() => taRef.current?.focus(), 50);
    }
  }, [open, defaultInstruction]);

  const generate = async () => {
    if (!precedingText || precedingText.trim().length < 30) {
      toast.error('Need at least 30 characters of preceding text to continue.');
      return;
    }
    setLoading(true);
    try {
      const data = await thesisApi.continueWriting(
        userId, projectId, precedingText, language, maxSentences,
        { instruction: instruction.trim() || null, followingText },
      );
      if (!data.continuation || !data.continuation.trim()) {
        toast.warning('AI returned an empty continuation. Try a different hint.');
        return;
      }
      setContinuation(data.continuation.trim());
      setMeta({ chunks_used: data.chunks_used,
                citations_available: data.citations_available || [] });
    } catch (e) {
      toast.error('Continue failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const accept = () => {
    if (!continuation) return;
    onInsert?.(continuation);
    onOpenChange(false);
  };

  const wordCount = continuation ? continuation.trim().split(/\s+/).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="smart-continue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[hsl(212,90%,55%)]" />
            Continue here
          </DialogTitle>
          <DialogDescription>
            Anchored after <strong>{anchorLabel}</strong>. The AI will read your preceding text,
            optionally follow your instruction, ground new claims in the chapter library, and produce
            a short continuation. You can preview, regenerate, or reject before it touches your manuscript.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Context preview — last 240 chars of preceding text */}
          {precedingText && (
            <div className="text-[11.5px] font-mono text-slate-500 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded px-3 py-2"
              data-testid="smart-continue-context">
              <span className="text-[9px] uppercase tracking-wider text-slate-400">PRECEDING TEXT</span>
              <div className="line-clamp-3">…{precedingText.slice(-240)}</div>
            </div>
          )}

          {/* Instruction */}
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1 block">
              Optional instruction
              <span className="text-[10px] text-slate-400 font-normal ml-2">(what should be added here?)</span>
            </label>
            <Textarea
              ref={taRef}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g. Add 2 sentences contrasting the Bakker 2012 trial with van Santvoort 2010 in terms of 90-day mortality."
              rows={3}
              className="text-sm"
              data-testid="smart-continue-instruction"
            />
          </div>

          {/* Length slider */}
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1 block">
              Length
              <span className="text-[10px] text-slate-400 font-normal ml-2">
                ~{maxSentences} sentence{maxSentences > 1 ? 's' : ''}
              </span>
            </label>
            <input
              type="range" min="1" max="5" value={maxSentences} step="1"
              onChange={(e) => setMaxSentences(Number(e.target.value))}
              className="w-full accent-[hsl(212,90%,55%)]"
              data-testid="smart-continue-length"
            />
          </div>

          {/* Generate / regenerate */}
          {!continuation && (
            <Button
              onClick={generate} disabled={loading}
              className="w-full bg-[hsl(212,90%,38%)] hover:bg-[hsl(212,90%,28%)] text-white"
              data-testid="smart-continue-generate">
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Generate continuation</>
              )}
            </Button>
          )}

          {/* Preview */}
          {continuation && (
            <div className="space-y-2" data-testid="smart-continue-preview-block">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-900 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                    AI Preview · {wordCount} words
                  </span>
                  {meta?.chunks_used != null && (
                    <span className="text-[10px] font-mono text-slate-500">
                      {meta.chunks_used} sources retrieved
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-wrap"
                  data-testid="smart-continue-preview-text">
                  {continuation}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={accept} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="smart-continue-accept">
                  <Check className="h-4 w-4 mr-1" /> Accept &amp; insert
                </Button>
                <Button variant="outline" onClick={generate} disabled={loading}
                  data-testid="smart-continue-regenerate">
                  {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  Regenerate
                </Button>
                <Button variant="ghost" onClick={() => setContinuation('')}
                  data-testid="smart-continue-reject">
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-slate-800 mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="smart-continue-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
